import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardContainer } from "routes/createContent/components/styles";
import { LibraryItem } from "../../components/LibraryItem/LibraryItem";
import { Link } from "react-router-dom";
import { Area } from "types";
import { useGetExercises } from "routes/createContent/api/useGetExercises";
import { Loading, PremiumIcon, CTable } from "shared";
import CCell from "shared/CTable/CCell";
import { FilterBox } from "routes/createContent/components/FilterBox";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TableCell,
  TablePagination,
  TextField,
} from "@material-ui/core";
import { ExerciseState, usePutExerciseState, Vars } from "routes/createContent/api/usePutExerciseState";
import { useDeleteExercise } from "routes/createContent/api/useDeleteExercise";
import { Position, usePutLevelPosition } from "routes/createContent/api/usePutLevelPosition";
import { Movement, usePutLevelMovement } from "routes/createContent/api/usePutLevelMovement";
import { DeleteExerciseDialog } from "routes/createContent/components/DeleteExerciseDialog";
import { CheckCircle, Cancel, Edit, FindInPage } from "@material-ui/icons";
import styled from "styled-components";
import { useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import { isUsMarket } from "utils/market";

const joints = [
  "hip",
  "knee",
  "lower_back",
  "upper_back",
  "hand",
  "shoulder",
  "neck",
  "foot",
  "elbow",
  "lower_leg",
  "wrist",
  "fall_prevention",
  "osteoporosis",
  "jaw",
  "neuro",
  "chest",
];

const positions = ["standing", "sitting", "lying"];

const movements = [
  "flexion",
  "extension",
  "adduction",
  "abduction",
  "internal_rotation",
  "external_rotation",
  "lateral_flexion",
];

const equipments = ["none", "band", "weight", "ball", "chair", "wall", "mat", "stick", "towel"];

interface EllipsizedCellProps {
  text: string;
  maxWidth: string;
  onHover: (text: string, x: number, y: number) => void;
  onLeave: () => void;
}

const EllipsizedCell: React.FC<EllipsizedCellProps> = ({ text, maxWidth, onHover, onLeave }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: text ? "pointer" : "default",
      }}
      onMouseEnter={e => {
        if (text) {
          const rect = e.currentTarget.getBoundingClientRect();
          onHover(text, rect.right + 10, rect.top);
        }
      }}
      onMouseLeave={onLeave}
    >
      {text || t("exercises.n_a")}
    </div>
  );
};

interface PositionSelectProps {
  value: string;
  onChange: (value: Position) => void;
  labelId?: string;
}

const PositionSelect: React.FC<PositionSelectProps> = ({ value, onChange, labelId }) => {
  const { t } = useTranslation();
  return (
    <Select value={value} onChange={e => onChange(e.target.value as Position)} labelId={labelId}>
      <MenuItem value="none">{t("exercises.position.none")}</MenuItem>
      <MenuItem value="standing">{t("exercises.position.standing")}</MenuItem>
      <MenuItem value="sitting">{t("exercises.position.sitting")}</MenuItem>
      <MenuItem value="lying">{t("exercises.position.lying")}</MenuItem>
    </Select>
  );
};

interface MovementSelectProps {
  value: string;
  onChange: (value: Movement) => void;
  labelId?: string;
}

const MovementSelect: React.FC<MovementSelectProps> = ({ value, onChange, labelId }) => {
  const { t } = useTranslation();
  return (
    <Select value={value} onChange={e => onChange(e.target.value as Movement)} labelId={labelId}>
      <MenuItem value="none">{t("exercises.movement.none")}</MenuItem>
      <MenuItem value="flexion">{t("exercises.movement.flexion")}</MenuItem>
      <MenuItem value="extension">{t("exercises.movement.extension")}</MenuItem>
      <MenuItem value="adduction">{t("exercises.movement.adduction")}</MenuItem>
      <MenuItem value="abduction">{t("exercises.movement.abduction")}</MenuItem>
      <MenuItem value="internal_rotation">{t("exercises.movement.internal_rotation")}</MenuItem>
      <MenuItem value="external_rotation">{t("exercises.movement.external_rotation")}</MenuItem>
      <MenuItem value="lateral_flexion">{t("exercises.movement.lateral_flexion")}</MenuItem>
    </Select>
  );
};

export const Exercises: React.VFC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedExerciseIDs, setSelectedExerciseIDs] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [limit, setLimit] = useState(25);

  const [locale, setLocale] = useState(isUsMarket ? "en-US" : "sv-SE");
  const [selectedJoints, setSelectedJoints] = useState<string[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedMovements, setSelectedMovements] = useState<string[]>([]);
  const [selectedEquipments, setSelectedEquipments] = useState<string[]>([]);
  const [view, setView] = useState<"Cards" | "Table">("Table");
  const [hoveredImage, setHoveredImage] = useState<{ src: string; x: number; y: number } | null>(null);
  const [hoveredAbout, setHoveredAbout] = useState<{ text: string; x: number; y: number } | null>(null);
  const [selectedState, setSelectedState] = useState<ExerciseState | "">("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data = {
      items: [],
      metadata: {
        total_count: 0,
      },
    },
    isError,
    isLoading,
  } = useGetExercises({
    search: debouncedSearchTerm,
    page: currentPage,
    limit: limit,
    locale: locale,
    joint: selectedJoints.length > 0 ? selectedJoints : undefined,
    state: selectedState || undefined,
    position: selectedPositions.length > 0 ? selectedPositions : undefined,
    movement: selectedMovements.length > 0 ? selectedMovements : undefined,
    equipment: selectedEquipments.length > 0 ? selectedEquipments : undefined,
  });
  const filteredExercises = data.items;

  const putExerciseState = usePutExerciseState();
  const putLevelPosition = usePutLevelPosition();
  const putLevelMovement = usePutLevelMovement();
  const deleteExercise = useDeleteExercise();

  const updateExerciseState = (args: Vars) => {
    putExerciseState.mutateAsync(
      { id: Number(args.id), state: args.state },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getExercises());
        },
      }
    );
  };

  const updateLevelPosition = (exercise: any, position: Position) => {
    const level = exercise.levels[0];
    if (!level) return;

    putLevelPosition.mutateAsync(
      { exerciseId: exercise.exercise_id, level, position },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getExercises());
        },
      }
    );
  };

  const updateLevelMovement = (exercise: any, movement: Movement) => {
    const level = exercise.levels[0];
    if (!level) return;

    putLevelMovement.mutateAsync(
      { exerciseId: exercise.exercise_id, level, movement },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getExercises());
        },
      }
    );
  };

  const updateMultipleExerciseStates = async (exerciseIds: number[], newState: ExerciseState) => {
    try {
      const updatePromises = exerciseIds.map(id => putExerciseState.mutateAsync({ id, state: newState }));
      await Promise.all(updatePromises);

      queryClient.invalidateQueries(queryKeys.getExercises());
      setSelectedExerciseIDs([]);
    } catch (error) {
      console.error("Failed to update some exercises:", error);
    }
  };

  const updateMultipleExercisePositions = async (exerciseIds: number[], newPosition: Position) => {
    try {
      const exercises = filteredExercises.filter((ex: any) => exerciseIds.includes(ex.exercise_id));
      const updatePromises = exercises.map((exercise: any) => {
        const level = exercise.levels[0];
        if (!level) return Promise.resolve();
        return putLevelPosition.mutateAsync({ exerciseId: exercise.exercise_id, level, position: newPosition });
      });
      await Promise.all(updatePromises);

      queryClient.invalidateQueries(queryKeys.getExercises());
      setSelectedExerciseIDs([]);
    } catch (error) {
      console.error("Failed to update some exercises:", error);
    }
  };

  const updateMultipleExerciseMovements = async (exerciseIds: number[], newMovement: Movement) => {
    try {
      const exercises = filteredExercises.filter((ex: any) => exerciseIds.includes(ex.exercise_id));
      const updatePromises = exercises.map((exercise: any) => {
        const level = exercise.levels[0];
        if (!level) return Promise.resolve();
        return putLevelMovement.mutateAsync({ exerciseId: exercise.exercise_id, level, movement: newMovement });
      });
      await Promise.all(updatePromises);

      queryClient.invalidateQueries(queryKeys.getExercises());
      setSelectedExerciseIDs([]);
    } catch (error) {
      console.error("Failed to update some exercises:", error);
    }
  };

  const handleExerciseSelect = (exerciseID: number) => {
    if (selectedExerciseIDs.includes(exerciseID)) {
      setSelectedExerciseIDs(selectedExerciseIDs.filter(id => id !== exerciseID));
    } else {
      setSelectedExerciseIDs([...selectedExerciseIDs, exerciseID]);
    }
  };

  const handleDeleteExercise = (exerciseId: number) => {
    deleteExercise.mutateAsync(
      { id: exerciseId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getExercises());
          setDeleteConfirmationOpen(null);
        },
        onError: error => {
          console.error("Failed to delete exercise:", error);
          setDeleteConfirmationOpen(null);
        },
      }
    );
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedJoints, selectedState, selectedPositions, selectedMovements, selectedEquipments]);

  return (
    <div>
      <h1>{t("exercises.title")}</h1>

      <Box mb={3} style={{ display: "flex", gap: "16px" }}>
        <Select value={locale} onChange={e => setLocale(e.target.value as string)} variant="outlined">
          <MenuItem value="sv-SE">{t("exercises.locale.sv_se")}</MenuItem>
          <MenuItem value="en-SE">{t("exercises.locale.en_se")}</MenuItem>
          <MenuItem value="en-GB">{t("exercises.locale.en_gb")}</MenuItem>
          <MenuItem value="en-US">{t("exercises.locale.en_us")}</MenuItem>
        </Select>
        <TextField
          fullWidth
          label={t("exercises.search_label")}
          variant="outlined"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder={t("exercises.search_placeholder")}
        />
      </Box>

      <FilterBox
        joints={joints}
        selectedJoints={selectedJoints}
        setSelectedJoints={setSelectedJoints}
        positions={positions}
        selectedPositions={selectedPositions}
        setSelectedPositions={setSelectedPositions}
        movements={movements}
        selectedMovements={selectedMovements}
        setSelectedMovements={setSelectedMovements}
        equipments={equipments}
        selectedEquipments={selectedEquipments}
        setSelectedEquipments={setSelectedEquipments}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        t={t}
      />

      <Box mb={3} style={{ position: "relative", zIndex: 1 }}>
        <FormControl component="fieldset" style={{ width: "100%" }}>
          <FormLabel component="legend">{t("exercises.view_label")}</FormLabel>
          <RadioGroup
            row
            value={view}
            onChange={e => setView(e.target.value as "Cards" | "Table")}
            style={{ marginTop: "8px" }}
          >
            <FormControlLabel
              value="Cards"
              control={<Radio color="primary" />}
              label={t("exercises.view.cards")}
              style={{ marginRight: "24px" }}
            />
            <FormControlLabel value="Table" control={<Radio color="primary" />} label={t("exercises.view.table")} />
          </RadioGroup>
        </FormControl>
      </Box>

      {isError && <div>{t("exercises.error_loading")}</div>}
      {isLoading && <Loading />}

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 2000]}
        component="div"
        count={data?.metadata?.total_count}
        rowsPerPage={limit}
        page={currentPage}
        onPageChange={(event, newPage) => {
          setCurrentPage(newPage);
        }}
        onRowsPerPageChange={event => {
          setLimit(parseInt(event.target.value, 10));
        }}
      />

      {selectedExerciseIDs.length > 0 && (
        <Box style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }} mb={3}>
          <Box>{t("exercises.selected_ids", { ids: selectedExerciseIDs.join(", ") })}</Box>
          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <InputLabel id="exercise-select-state">{t("exercises.change_state_to")}</InputLabel>
            <Select
              labelId="exercise-select-state"
              onChange={e => {
                updateMultipleExerciseStates(selectedExerciseIDs, e.target.value as ExerciseState);
              }}
            >
              <MenuItem value="draft">
                <StyledBox $color="orange">
                  {t("exercises.state.draft")} <Edit />
                </StyledBox>
              </MenuItem>
              <MenuItem value="in_review">
                <StyledBox $color="blue">
                  {t("exercises.state.in_review")} <FindInPage />
                </StyledBox>
              </MenuItem>
              <MenuItem value="approved">
                <StyledBox $color="green">
                  {t("exercises.state.approved")} <CheckCircle />
                </StyledBox>
              </MenuItem>
              <MenuItem value="rejected">
                <StyledBox $color="red">
                  {t("exercises.state.rejected")} <Cancel />
                </StyledBox>
              </MenuItem>
            </Select>
          </Box>
          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <InputLabel id="exercise-select-position">{t("exercises.change_position_to")}</InputLabel>
            <PositionSelect
              value=""
              onChange={value => updateMultipleExercisePositions(selectedExerciseIDs, value)}
              labelId="exercise-select-position"
            />
          </Box>
          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <InputLabel id="exercise-select-movement">{t("exercises.change_movement_to")}</InputLabel>
            <MovementSelect
              value=""
              onChange={value => updateMultipleExerciseMovements(selectedExerciseIDs, value)}
              labelId="exercise-select-movement"
            />
          </Box>
        </Box>
      )}

      {!isLoading && filteredExercises.length === 0 ? (
        <Box textAlign="center" py={4}>
          <p>{t("exercises.no_exercises_found", { searchTerm })}</p>
        </Box>
      ) : view === "Cards" ? (
        <CardContainer>
          {filteredExercises?.map((exercise: any) => {
            const { exercise_id, premium, state, target_joints } = exercise;
            const { id, title } = exercise.levels[0];
            const caption =
              exercise.levels.length > 0 ? `${exercise.levels.length} ${t("usercourse.levels")}` : undefined;
            const image = exercise.levels[0].image_url
              ? exercise.levels[0].image_url
              : exercise.levels[0].video_thumbnail_file_url;

            return (
              <LibraryItem
                key={`exercise_${exercise_id}`}
                bottomContent={
                  <Box
                    style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                    onClick={e => e.stopPropagation()}
                  >
                    <Box style={{ display: "flex", gap: "8px", flex: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        component={Link as any}
                        to={`${Area.CreateContent_Exercises}/${exercise_id}`}
                        style={{ flex: 1 }}
                      >
                        {t("exercises.button.view")}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        component={Link as any}
                        to={`${Area.CreateContent_Exercises}/${exercise_id}/edit`}
                        style={{ flex: 1 }}
                      >
                        {t("exercises.button.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => setDeleteConfirmationOpen(exercise_id)}
                        style={{ flex: 1 }}
                      >
                        {t("exercises.button.delete")}
                      </Button>
                    </Box>
                    <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Select
                        disabled={exercise.state === "approved"}
                        value={state}
                        onChange={e => {
                          updateExerciseState({ id: exercise_id, state: e.target.value as Vars["state"] });
                        }}
                      >
                        <MenuItem value="draft">
                          <StyledBox $color="orange">
                            {t("exercises.state.draft")} <Edit />
                          </StyledBox>
                        </MenuItem>
                        <MenuItem value="in_review">
                          <StyledBox $color="blue">
                            {t("exercises.state.in_review")} <FindInPage />
                          </StyledBox>
                        </MenuItem>
                        <MenuItem value="approved">
                          <StyledBox $color="green">
                            {t("exercises.state.approved")} <CheckCircle />
                          </StyledBox>
                        </MenuItem>
                        <MenuItem value="rejected">
                          <StyledBox $color="red">
                            {t("exercises.state.rejected")} <Cancel />
                          </StyledBox>
                        </MenuItem>
                      </Select>
                      {putExerciseState.isLoading && putExerciseState.variables?.id === exercise_id && (
                        <CircularProgress size={24} />
                      )}
                    </Box>
                  </Box>
                }
                caption={caption}
                icon={premium ? <PremiumIcon size="small" premiumType="guided_care" /> : undefined}
                id={id}
                image={<img alt="" src={image || ""} height="100%" />}
                tags={target_joints}
                title={title}
                selected={selectedExerciseIDs.includes(exercise_id)}
                onClick={() => handleExerciseSelect(exercise_id)}
              />
            );
          })}
        </CardContainer>
      ) : (
        <CTable
          style={{
            width: "100vw",
            left: 0,
            position: "absolute",
          }}
          header={{
            exercise_id: t("exercises.table.exercise_id"),
            title: t("exercises.table.title"),
            about: t("exercises.table.about"),
            image: t("exercises.table.image"),
            position: t("exercises.table.position"),
            movement: t("exercises.table.movement"),
            state: t("exercises.table.state"),
            target_joints: t("exercises.table.target_joints"),
            updated_at: t("exercises.table.updated_at"),
            selected: t("exercises.table.select"),
            actions: t("exercises.table.actions"),
          }}
          items={filteredExercises?.map((exercise: any) => ({
            id: exercise.exercise_id,
            exercise_id: exercise.exercise_id,
            title: exercise.levels[0]?.title || "",
            about: exercise.levels[0]?.about || "",
            image: exercise.levels[0]?.image_url || exercise.levels[0]?.video_thumbnail_file_url || "",
            position: exercise.levels[0]?.position,
            movement: exercise.levels[0]?.movement,
            state: exercise.state,
            target_joints: exercise.target_joints,
            updated_at: exercise.updated_at,
            selected: selectedExerciseIDs.includes(exercise.exercise_id),
            actions: exercise.exercise_id,
            exercise: exercise,
          }))}
          cellFactory={(item: any) => ({
            exercise_id: <CCell>{item.exercise_id}</CCell>,
            title: (
              <CCell>
                <EllipsizedCell
                  text={item.title}
                  maxWidth="200px"
                  onHover={(text, x, y) => setHoveredAbout({ text, x, y })}
                  onLeave={() => setHoveredAbout(null)}
                />
              </CCell>
            ),
            about: (
              <CCell>
                <EllipsizedCell
                  text={item.about}
                  maxWidth="200px"
                  onHover={(text, x, y) => setHoveredAbout({ text, x, y })}
                  onLeave={() => setHoveredAbout(null)}
                />
              </CCell>
            ),
            image: (
              <CCell>
                <img
                  alt=""
                  src={item.image}
                  style={{ width: "60px", height: "40px", objectFit: "cover", cursor: "pointer" }}
                  onMouseEnter={e => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredImage({
                      src: item.image,
                      x: rect.right + 10,
                      y: rect.top,
                    });
                  }}
                  onMouseLeave={() => setHoveredImage(null)}
                />
              </CCell>
            ),
            position: (
              <TableCell>
                <PositionSelect
                  value={item.position || "none"}
                  onChange={value => updateLevelPosition(item.exercise, value)}
                />
                {putLevelPosition.isLoading && putLevelPosition.variables?.exerciseId === item.exercise_id && (
                  <CircularProgress size={20} />
                )}
              </TableCell>
            ),
            movement: (
              <TableCell>
                <MovementSelect
                  value={item.movement || "none"}
                  onChange={value => updateLevelMovement(item.exercise, value)}
                />
                {putLevelMovement.isLoading && putLevelMovement.variables?.exerciseId === item.exercise_id && (
                  <CircularProgress size={20} />
                )}
              </TableCell>
            ),
            state: (
              <TableCell>
                <Select
                  disabled={item.exercise.state === "approved"}
                  value={item.state}
                  onChange={e => {
                    updateExerciseState({ id: item.exercise_id, state: e.target.value as Vars["state"] });
                  }}
                >
                  <MenuItem value="draft">
                    <StyledBox $color="orange">
                      {t("exercises.state.draft")} <Edit />
                    </StyledBox>
                  </MenuItem>
                  <MenuItem value="in_review">
                    <StyledBox $color="blue">
                      {t("exercises.state.in_review")} <FindInPage />
                    </StyledBox>
                  </MenuItem>
                  <MenuItem value="approved">
                    <StyledBox $color="green">
                      {t("exercises.state.approved")} <CheckCircle />
                    </StyledBox>
                  </MenuItem>
                  <MenuItem value="rejected">
                    <StyledBox $color="red">
                      {t("exercises.state.rejected")} <Cancel />
                    </StyledBox>
                  </MenuItem>
                </Select>
                {putExerciseState.isLoading && putExerciseState.variables?.id === item.exercise_id && (
                  <CircularProgress size={20} />
                )}
              </TableCell>
            ),
            target_joints: (
              <CCell>
                <EllipsizedCell
                  text={Array.isArray(item.target_joints) ? item.target_joints.join(", ") : item.target_joints}
                  maxWidth="150px"
                  onHover={(text, x, y) => setHoveredAbout({ text, x, y })}
                  onLeave={() => setHoveredAbout(null)}
                />
              </CCell>
            ),
            updated_at: (
              <CCell>
                {item.updated_at
                  ? new Date(item.updated_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : t("exercises.n_a")}
              </CCell>
            ),
            selected: (
              <CCell>
                <Checkbox checked={item.selected} onChange={() => handleExerciseSelect(item.exercise_id)} />
              </CCell>
            ),
            actions: (
              <CCell>
                <Box style={{ display: "flex", gap: "8px" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    component={Link as any}
                    to={`${Area.CreateContent_Exercises}/${item.exercise_id}`}
                  >
                    {t("exercises.button.view")}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    component={Link as any}
                    to={`${Area.CreateContent_Exercises}/${item.exercise_id}/edit`}
                  >
                    {t("exercises.button.edit")}
                  </Button>
                  <Button variant="contained" size="small" onClick={() => setDeleteConfirmationOpen(item.exercise_id)}>
                    {t("exercises.button.delete")}
                  </Button>
                </Box>
              </CCell>
            ),
          })}
        />
      )}

      {hoveredImage && (
        <div
          style={{
            position: "fixed",
            left: hoveredImage.x - 320,
            top: hoveredImage.y - 240,
            zIndex: 1000,
            backgroundColor: "white",
            border: "2px solid #ccc",
            borderRadius: "8px",
            padding: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            pointerEvents: "none",
          }}
        >
          <img
            alt=""
            src={hoveredImage.src}
            style={{
              width: "640px",
              height: "480px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        </div>
      )}

      {hoveredAbout && (
        <div
          style={{
            position: "fixed",
            left: hoveredAbout.x,
            top: hoveredAbout.y,
            zIndex: 1001,
            backgroundColor: "white",
            border: "2px solid #ccc",
            borderRadius: "8px",
            padding: "16px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            pointerEvents: "none",
            maxWidth: "450px",
            lineHeight: "1.5",
            fontSize: "16px",
          }}
        >
          {hoveredAbout.text}
        </div>
      )}

      <DeleteExerciseDialog
        open={deleteConfirmationOpen !== null}
        onClose={() => setDeleteConfirmationOpen(null)}
        onConfirm={() => deleteConfirmationOpen && handleDeleteExercise(deleteConfirmationOpen)}
        isLoading={deleteExercise.isLoading}
      />
    </div>
  );
};

const StyledBox = styled(Box)<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: ${({ $color }) => $color || "inherit"};
  }
`;
